import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableBody,
  TableCell,
  TableRow,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@saleor/components/Checkbox";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import PriceField from "@saleor/components/PriceField";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import TableCellAvatar from "@saleor/components/TableCellAvatar";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchProductVariants_search_edges_node } from "@saleor/searches/types/SearchProductVariants";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { FormattedMessage, useIntl } from "react-intl";

export interface FormData {
  variants: SearchProductVariants_search_edges_node[];
  query: string;
}

const useStyles = makeStyles(
  {
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colName: {
      paddingLeft: 0
    }
  },
  { name: "AssignVariantDialogSetPrice" }
);

export interface AssignVariantDialogProps extends FetchMoreProps {
  currencySymbol?: string;
  confirmButtonState: ConfirmButtonTransitionState;
  open: boolean;
  variants: SearchProductVariants_search_edges_node[];
  loading: boolean;
  onClose: () => void;
  onFetch: (value: string) => void;
  onSubmit: (
    data: SearchProductVariants_search_edges_node[],
    price: number
  ) => void;
  confirmButtonState2?: ConfirmButtonTransitionState;
  onSubmit2?: (data: SearchProductVariants_search_edges_node[]) => void;
}

function handleVariantAssign(
  variant: SearchProductVariants_search_edges_node,
  isSelected: boolean,
  selectedVariants: SearchProductVariants_search_edges_node[],
  setSelectedVariants: (data: SearchProductVariants_search_edges_node[]) => void
) {
  if (isSelected) {
    setSelectedVariants(
      selectedVariants.filter(
        selectedVariant => selectedVariant.id !== variant.id
      )
    );
  } else {
    setSelectedVariants([...selectedVariants, variant]);
  }
}

function handleVariantsAssign(
  variants: SearchProductVariants_search_edges_node[],
  selectedVariants: SearchProductVariants_search_edges_node[],
  setSelectedVariants: (data: SearchProductVariants_search_edges_node[]) => void
) {
  if (selectedVariants.length === variants.length) {
    setSelectedVariants([]);
  } else {
    setSelectedVariants(variants);
  }
}

const scrollableTargetId = "assignVariantScrollableDialog";

const AssignVariantDialogSetPrice: React.FC<AssignVariantDialogProps> = props => {
  const {
    currencySymbol,
    confirmButtonState,
    hasMore,
    open,
    loading,
    variants,
    onClose,
    onFetch,
    onFetchMore,
    onSubmit,
    confirmButtonState2,
    onSubmit2
  } = props;
  const classes = useStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});
  const intl = useIntl();
  const [query, onQueryChange] = useSearchQuery(onFetch);
  const [price, setPrice] = React.useState<number>(0);
  const [selectedVariants, setSelectedVariants] = React.useState<
    SearchProductVariants_search_edges_node[]
  >([]);
  const handleSubmit = () => onSubmit(selectedVariants, price);
  const handleSubmit2 = () =>
    onSubmit2 ? onSubmit2(selectedVariants) : onSubmit(selectedVariants, price);
  return (
    <Dialog
      onClose={onClose}
      open={open}
      classes={{ paper: scrollableDialogClasses.dialog }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <FormattedMessage
          defaultMessage="Assign SKU"
          description="dialog header"
        />
        <PriceField
          name="price"
          label={intl.formatMessage({
            defaultMessage: "Price"
          })}
          value={price}
          currencySymbol={currencySymbol}
          onChange={e => {
            setPrice(e.target.value);
          }}
          disabled={loading}
          InputProps={{
            inputProps: {
              min: "0"
            }
          }}
        />
      </DialogTitle>
      <DialogContent className={scrollableDialogClasses.topArea}>
        <TextField
          name="query"
          value={query}
          onChange={onQueryChange}
          label={intl.formatMessage({
            defaultMessage: "Search  SKU"
          })}
          placeholder={intl.formatMessage({
            defaultMessage:
              "Search by  sku,sku name, attribute, product name etc..."
          })}
          fullWidth
          InputProps={{
            autoComplete: "off",
            endAdornment: loading && <CircularProgress size={16} />
          }}
        />
      </DialogContent>
      <DialogContent
        className={scrollableDialogClasses.scrollArea}
        id={scrollableTargetId}
      >
        <InfiniteScroll
          dataLength={variants?.length}
          next={onFetchMore}
          hasMore={hasMore}
          scrollThreshold="100px"
          loader={
            <div className={scrollableDialogClasses.loadMoreLoaderContainer}>
              <CircularProgress size={16} />
            </div>
          }
          scrollableTarget={scrollableTargetId}
        >
          <ResponsiveTable key="table">
            <TableBody>
              {variants &&
                variants.map(variant => {
                  const isSelected = selectedVariants.some(
                    selectedVariant => selectedVariant.id === variant.id
                  );

                  return (
                    <TableRow
                      key={variant.id}
                      data-test-id="assign-variant-table-row"
                    >
                      <TableCellAvatar
                        className={classes.avatar}
                        thumbnail={maybe(() => variant.thumbnail.url)}
                      />
                      <TableCell className={classes.colName}>
                        {variant.product.name}
                      </TableCell>
                      <TableCell className={classes.colName}>
                        {variant.sku}
                        <br />
                        {variant.name}
                      </TableCell>
                      <TableCell className={classes.colName}>
                        {currencySymbol +
                          " " +
                          maybe(() => variant?.price.amount, 0)}
                      </TableCell>
                      <TableCell
                        padding="checkbox"
                        className={classes.checkboxCell}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() =>
                            handleVariantAssign(
                              variant,
                              isSelected,
                              selectedVariants,
                              setSelectedVariants
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </ResponsiveTable>
        </InfiniteScroll>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            handleVariantsAssign(
              variants,
              selectedVariants,
              setSelectedVariants
            );
          }}
          style={{ float: "left" }}
        >
          <FormattedMessage defaultMessage="Select All" description="button" />
        </Button>
        <Button onClick={onClose}>
          <FormattedMessage {...buttonMessages.back} />
        </Button>
        {onSubmit2 && (
          <ConfirmButton
            data-test="submit"
            transitionState={confirmButtonState2}
            color="primary"
            variant="contained"
            type="submit"
            onClick={() => {
              setSelectedVariants([]);
              handleSubmit2();
            }}
          >
            <FormattedMessage
              defaultMessage="UnAssign SKU"
              description="button"
            />
          </ConfirmButton>
        )}
        <ConfirmButton
          data-test="submit"
          transitionState={confirmButtonState}
          color="primary"
          variant="contained"
          type="submit"
          onClick={() => {
            setSelectedVariants([]);
            handleSubmit();
          }}
        >
          <FormattedMessage defaultMessage="Assign SKU" description="button" />
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
};
AssignVariantDialogSetPrice.displayName = "AssignVariantDialogSetPrice";
export default AssignVariantDialogSetPrice;
